import React, { useState, useMemo } from "react";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { TrendingUp, Calculator, Sparkles, Info, Wallet, PiggyBank, ArrowRight } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CalculatorSection = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [timePeriod, setTimePeriod] = useState(15);
  const [stepUpEnabled, setStepUpEnabled] = useState(false);
  const [calculatorMode, setCalculatorMode] = useState("sip"); // "sip" or "retirement"
  
  // Retirement mode states
  const [retirementCorpus, setRetirementCorpus] = useState(10000000);
  const [withdrawalRate, setWithdrawalRate] = useState(8);
  const [growthRate, setGrowthRate] = useState(10);

  // Calculate SIP returns
  const calculations = useMemo(() => {
    const monthlyRate = expectedReturn / 100 / 12;
    const months = timePeriod * 12;
    let totalInvested = 0;
    let futureValue = 0;
    const chartData = [];

    if (stepUpEnabled) {
      // Step-up SIP: 10% annual increase
      let currentMonthly = monthlyInvestment;
      for (let year = 1; year <= timePeriod; year++) {
        const yearlyInvestment = currentMonthly * 12;
        totalInvested += yearlyInvestment;
        
        // Calculate FV for this year's investment
        const monthsRemaining = (timePeriod - year) * 12 + 6; // Average months remaining
        for (let m = 0; m < 12; m++) {
          const monthsToMature = (timePeriod - year) * 12 + (12 - m);
          futureValue += currentMonthly * Math.pow(1 + monthlyRate, monthsToMature);
        }
        
        chartData.push({
          year: `Year ${year}`,
          invested: Math.round(totalInvested),
          wealth: Math.round(futureValue),
        });
        
        currentMonthly = Math.round(currentMonthly * 1.1); // 10% increase
      }
    } else {
      // Regular SIP
      totalInvested = monthlyInvestment * months;
      futureValue =
        monthlyInvestment *
        ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
        (1 + monthlyRate);

      for (let year = 1; year <= timePeriod; year++) {
        const yearMonths = year * 12;
        const yearInvested = monthlyInvestment * yearMonths;
        const yearValue =
          monthlyInvestment *
          ((Math.pow(1 + monthlyRate, yearMonths) - 1) / monthlyRate) *
          (1 + monthlyRate);
        chartData.push({
          year: `Year ${year}`,
          invested: Math.round(yearInvested),
          wealth: Math.round(yearValue),
        });
      }
    }

    const wealthGain = futureValue - totalInvested;

    return {
      totalInvested: Math.round(totalInvested),
      futureValue: Math.round(futureValue),
      wealthGain: Math.round(wealthGain),
      chartData,
    };
  }, [monthlyInvestment, expectedReturn, timePeriod, stepUpEnabled]);

  // Retirement/SWP calculations
  const retirementCalc = useMemo(() => {
    const monthlyIncome = Math.round((retirementCorpus * (withdrawalRate / 100)) / 12);
    const annualWithdrawal = retirementCorpus * (withdrawalRate / 100);
    const netGrowth = growthRate - withdrawalRate;
    
    // Simulate 30 years of SWP
    const swpData = [];
    let currentCorpus = retirementCorpus;
    
    for (let year = 0; year <= 30; year++) {
      swpData.push({
        year: `Year ${year}`,
        corpus: Math.round(currentCorpus),
        withdrawn: year === 0 ? 0 : Math.round(annualWithdrawal * year),
      });
      
      // Apply growth and withdrawal
      currentCorpus = currentCorpus * (1 + growthRate / 100) - annualWithdrawal;
      if (currentCorpus < 0) currentCorpus = 0;
    }
    
    const yearsToDepletion = netGrowth >= 0 ? "Indefinite" : 
      Math.floor(retirementCorpus / (annualWithdrawal - retirementCorpus * growthRate / 100));
    
    return {
      monthlyIncome,
      annualWithdrawal: Math.round(annualWithdrawal),
      corpusAfter30Years: Math.round(currentCorpus),
      yearsToDepletion,
      swpData,
      willGrow: netGrowth > 0,
    };
  }, [retirementCorpus, withdrawalRate, growthRate]);

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    } else {
      return `₹${value.toLocaleString("en-IN")}`;
    }
  };

  return (
    <section
      id="calculator"
      className="py-24 sm:py-32 bg-gradient-to-b from-white via-emerald-50/50 to-white"
      data-testid="calculator-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            The 8th Wonder of the World
          </span>
          <h2
            className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-emerald-950 mb-4"
            data-testid="calculator-title"
          >
            Experience the Power of Compounding
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Albert Einstein called compound interest the 8th wonder of the world.{" "}
            <strong>Those who understand it, earn it; those who don't, pay it.</strong>{" "}
            See how your money can grow exponentially over time.
          </p>
        </div>

        {/* Calculator Mode Tabs */}
        <Tabs value={calculatorMode} onValueChange={setCalculatorMode} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-emerald-100 p-1 rounded-full">
            <TabsTrigger 
              value="sip" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-md"
              data-testid="tab-sip-mode"
            >
              <PiggyBank className="w-4 h-4 mr-2" />
              Wealth Builder (SIP)
            </TabsTrigger>
            <TabsTrigger 
              value="retirement" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-md"
              data-testid="tab-retirement-mode"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Income Mode (SWP)
            </TabsTrigger>
          </TabsList>

          {/* SIP Calculator Tab */}
          <TabsContent value="sip" className="mt-0">
        {/* Calculator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Controls */}
          <div className="lg:col-span-5">
            <div className="glass-card rounded-3xl p-6 md:p-8 shadow-xl shadow-emerald-900/5">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-900">
                    SIP Calculator
                  </h3>
                  <p className="text-sm text-slate-500">Plan your wealth journey</p>
                </div>
              </div>

              {/* Monthly Investment Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-sm font-medium text-slate-700">
                    Monthly SIP Amount
                  </Label>
                  <span
                    className="text-lg font-bold text-emerald-600"
                    data-testid="monthly-investment-value"
                  >
                    ₹{monthlyInvestment.toLocaleString("en-IN")}
                  </span>
                </div>
                <Slider
                  value={[monthlyInvestment]}
                  onValueChange={(value) => setMonthlyInvestment(value[0])}
                  min={1000}
                  max={100000}
                  step={1000}
                  className="w-full"
                  data-testid="monthly-investment-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>₹1,000</span>
                  <span>₹1,00,000</span>
                </div>
              </div>

              {/* Expected Return Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-sm font-medium text-slate-700">
                    Expected Annual Return (CAGR)
                  </Label>
                  <span
                    className="text-lg font-bold text-emerald-600"
                    data-testid="expected-return-value"
                  >
                    {expectedReturn}%
                  </span>
                </div>
                <Slider
                  value={[expectedReturn]}
                  onValueChange={(value) => setExpectedReturn(value[0])}
                  min={8}
                  max={18}
                  step={0.5}
                  className="w-full"
                  data-testid="expected-return-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>8%</span>
                  <span>18%</span>
                </div>
              </div>

              {/* Time Period Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-sm font-medium text-slate-700">
                    Investment Period
                  </Label>
                  <span
                    className="text-lg font-bold text-emerald-600"
                    data-testid="time-period-value"
                  >
                    {timePeriod} Years
                  </span>
                </div>
                <Slider
                  value={[timePeriod]}
                  onValueChange={(value) => setTimePeriod(value[0])}
                  min={1}
                  max={30}
                  step={1}
                  className="w-full"
                  data-testid="time-period-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>1 Year</span>
                  <span>30 Years</span>
                </div>
              </div>

              {/* Step-up SIP Toggle */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <div>
                    <Label
                      htmlFor="step-up"
                      className="text-sm font-medium text-emerald-900 cursor-pointer"
                    >
                      Enable Step-up SIP
                    </Label>
                    <p className="text-xs text-slate-500">
                      Increase investment by 10% yearly
                    </p>
                  </div>
                </div>
                <Switch
                  id="step-up"
                  checked={stepUpEnabled}
                  onCheckedChange={setStepUpEnabled}
                  data-testid="step-up-toggle"
                />
              </div>

              {/* CAGR Info */}
              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Average Mutual Fund CAGR
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Large Cap: 10-12% | Flexi Cap: 12-14% | Mid Cap: 13-15% | Small Cap: 14-18%
                    </p>
                    <p className="text-xs text-amber-600 mt-1 italic">
                      *Past performance doesn't guarantee future returns
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Results & Chart */}
          <div className="lg:col-span-7">
            {/* Results Cards */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 mb-8">
              <div
                className="result-box rounded-2xl p-5 md:p-6"
                data-testid="total-invested-result"
              >
                <p className="text-sm text-slate-600 mb-1">Total Invested</p>
                <p className="text-2xl md:text-3xl font-bold text-emerald-900 wealth-number">
                  {formatCurrency(calculations.totalInvested)}
                </p>
              </div>
              <div
                className="bg-emerald-500 rounded-2xl p-5 md:p-6 text-white"
                data-testid="wealth-created-result"
              >
                <p className="text-sm text-emerald-100 mb-1">Wealth Created</p>
                <p className="text-2xl md:text-3xl font-bold wealth-number">
                  {formatCurrency(calculations.futureValue)}
                </p>
              </div>
            </div>

            {/* Wealth Gain Highlight */}
            <div
              className="bg-gradient-to-r from-emerald-50 to-amber-50 rounded-2xl p-5 md:p-6 mb-8 border border-emerald-100"
              data-testid="wealth-gain-result"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-slate-600">Your Wealth Gain (Interest Earned)</p>
                  <p className="text-3xl md:text-4xl font-bold text-emerald-600 wealth-number">
                    {formatCurrency(calculations.wealthGain)}
                  </p>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-sm text-slate-600">Growth Multiplier</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {(calculations.futureValue / calculations.totalInvested).toFixed(1)}x
                  </p>
                </div>
              </div>
            </div>

            {/* Growth Chart */}
            <div
              className="glass-card rounded-3xl p-4 md:p-6 shadow-xl shadow-emerald-900/5"
              data-testid="wealth-chart"
            >
              <h4 className="text-sm font-medium text-slate-700 mb-4">
                Your Wealth Growth Journey
              </h4>
              <div className="h-[280px] md:h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={calculations.chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorWealth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="year"
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                      tickFormatter={(value) =>
                        value >= 10000000
                          ? `${(value / 10000000).toFixed(0)}Cr`
                          : value >= 100000
                          ? `${(value / 100000).toFixed(0)}L`
                          : `${(value / 1000).toFixed(0)}K`
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Area
                      type="monotone"
                      dataKey="invested"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      fill="url(#colorInvested)"
                      name="Total Invested"
                    />
                    <Area
                      type="monotone"
                      dataKey="wealth"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="url(#colorWealth)"
                      name="Wealth Created"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400" />
                  <span className="text-xs text-slate-600">Total Invested</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs text-slate-600">Wealth Created</span>
                </div>
              </div>
            </div>
          </div>
        </div>
          </TabsContent>

          {/* Retirement/SWP Calculator Tab */}
          <TabsContent value="retirement" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Left: Controls */}
              <div className="lg:col-span-5">
                <div className="glass-card rounded-3xl p-6 md:p-8 shadow-xl shadow-emerald-900/5">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-900">
                        Retirement Income Calculator
                      </h3>
                      <p className="text-sm text-slate-500">Your self-made pension (SWP)</p>
                    </div>
                  </div>

                  {/* Corpus Slider */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-slate-700">
                        Your Retirement Corpus
                      </Label>
                      <span className="text-lg font-bold text-emerald-600" data-testid="retirement-corpus-value">
                        {formatCurrency(retirementCorpus)}
                      </span>
                    </div>
                    <Slider
                      value={[retirementCorpus]}
                      onValueChange={(value) => setRetirementCorpus(value[0])}
                      min={2500000}
                      max={100000000}
                      step={500000}
                      className="w-full"
                      data-testid="retirement-corpus-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>₹25 Lakh</span>
                      <span>₹10 Crore</span>
                    </div>
                  </div>

                  {/* Withdrawal Rate Slider */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-slate-700">
                        Annual Withdrawal Rate
                      </Label>
                      <span className="text-lg font-bold text-emerald-600" data-testid="withdrawal-rate-value">
                        {withdrawalRate}%
                      </span>
                    </div>
                    <Slider
                      value={[withdrawalRate]}
                      onValueChange={(value) => setWithdrawalRate(value[0])}
                      min={4}
                      max={12}
                      step={0.5}
                      className="w-full"
                      data-testid="withdrawal-rate-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>4% (Conservative)</span>
                      <span>12% (Aggressive)</span>
                    </div>
                  </div>

                  {/* Expected Growth Slider */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-slate-700">
                        Expected Fund Growth (CAGR)
                      </Label>
                      <span className="text-lg font-bold text-emerald-600" data-testid="growth-rate-value">
                        {growthRate}%
                      </span>
                    </div>
                    <Slider
                      value={[growthRate]}
                      onValueChange={(value) => setGrowthRate(value[0])}
                      min={6}
                      max={15}
                      step={0.5}
                      className="w-full"
                      data-testid="growth-rate-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>6%</span>
                      <span>15%</span>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">
                          What is SWP?
                        </p>
                        <p className="text-xs text-emerald-700 mt-1">
                          Systematic Withdrawal Plan lets you withdraw a fixed amount monthly 
                          from your mutual fund while the remaining corpus continues to grow.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Results */}
              <div className="lg:col-span-7">
                {/* Results Cards */}
                <div className="grid grid-cols-2 gap-4 md:gap-6 mb-8">
                  <div className="result-box rounded-2xl p-5 md:p-6" data-testid="monthly-income-result">
                    <p className="text-sm text-slate-600 mb-1">Your Monthly Income</p>
                    <p className="text-2xl md:text-3xl font-bold text-emerald-600 wealth-number">
                      ₹{retirementCalc.monthlyIncome.toLocaleString("en-IN")}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      (₹{retirementCalc.annualWithdrawal.toLocaleString("en-IN")}/year)
                    </p>
                  </div>
                  <div
                    className={`rounded-2xl p-5 md:p-6 text-white ${
                      retirementCalc.willGrow ? "bg-emerald-500" : "bg-amber-500"
                    }`}
                    data-testid="corpus-projection-result"
                  >
                    <p className="text-sm opacity-80 mb-1">Corpus After 30 Years</p>
                    <p className="text-2xl md:text-3xl font-bold wealth-number">
                      {formatCurrency(retirementCalc.corpusAfter30Years)}
                    </p>
                    <p className="text-xs opacity-80 mt-1">
                      {retirementCalc.willGrow ? "Growing!" : "Depleting"}
                    </p>
                  </div>
                </div>

                {/* Key Insight */}
                <div
                  className={`rounded-2xl p-5 md:p-6 mb-8 border ${
                    retirementCalc.willGrow 
                      ? "bg-emerald-50 border-emerald-200" 
                      : "bg-amber-50 border-amber-200"
                  }`}
                  data-testid="swp-insight"
                >
                  <div className="flex items-start gap-3">
                    {retirementCalc.willGrow ? (
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                    ) : (
                      <Info className="w-6 h-6 text-amber-600" />
                    )}
                    <div>
                      <p className={`font-semibold ${retirementCalc.willGrow ? "text-emerald-800" : "text-amber-800"}`}>
                        {retirementCalc.willGrow 
                          ? "Your corpus will GROW while you withdraw!" 
                          : "Your corpus will deplete over time"}
                      </p>
                      <p className={`text-sm mt-1 ${retirementCalc.willGrow ? "text-emerald-700" : "text-amber-700"}`}>
                        {retirementCalc.willGrow 
                          ? `At ${withdrawalRate}% withdrawal from ${growthRate}% growth, your money lasts indefinitely!`
                          : `Consider reducing withdrawal rate to ${growthRate}% or below for sustainable income.`
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="glass-card rounded-3xl p-4 md:p-6 shadow-xl shadow-emerald-900/5" data-testid="swp-chart">
                  <h4 className="text-sm font-medium text-slate-700 mb-4">
                    Your Corpus Projection (30 Years)
                  </h4>
                  <div className="h-[280px] md:h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={retirementCalc.swpData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorCorpus" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="year"
                          tick={{ fontSize: 10, fill: "#64748b" }}
                          tickLine={false}
                          axisLine={{ stroke: "#e2e8f0" }}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: "#64748b" }}
                          tickLine={false}
                          axisLine={{ stroke: "#e2e8f0" }}
                          tickFormatter={(value) =>
                            value >= 10000000
                              ? `${(value / 10000000).toFixed(0)}Cr`
                              : value >= 100000
                              ? `${(value / 100000).toFixed(0)}L`
                              : `${(value / 1000).toFixed(0)}K`
                          }
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Area
                          type="monotone"
                          dataKey="corpus"
                          stroke="#10b981"
                          strokeWidth={2}
                          fill="url(#colorCorpus)"
                          name="Remaining Corpus"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default CalculatorSection;
